(ns rothko.styles.vars.common
  (:require [garden.color :as color :refer [rgb]]
            [garden.units :as units]
            #?(:cljs [goog.string :refer [format]])))

;; There is a bug in `com.yahoo.platform.yui.compressor` that strips the percent
;; sign from `0%` and due to the implementation of garden pre 2.0.0 all colours
;; are represented internally as hsla. This basically means that we can't use
;; `(garden.color/rgba 0 0 0 0.5)` in minified CSS (without monkeypatching
;; `garden.compiler/render-color`). This colour function avoids the issue;
;; however, this doesn't work with `rgb` as a string is not a valid garden
;; colour representation.
;; See: https://github.com/yui/yuicompressor/issues/80
(defn rgba
  [r g b a]
  (format "rgba(%s, %s, %s, %s);" r g b a))

(def ^:private colour-black (rgb 0 0 0))
(def ^:private colour-white (rgb 255 255 255))

;; Colours common to all themes
(def colours
  {:white colour-white
   :black colour-black
   :grey-3  (color/darken colour-white 3)
   :grey-5  (color/darken colour-white 5)
   :grey-10 (color/darken colour-white 10)
   :grey-20 (color/darken colour-white 20)
   :grey-30 (color/darken colour-white 30)
   :grey-40 (color/darken colour-white 40)
   :grey-50 (color/darken colour-white 50)
   :grey-60 (color/darken colour-white 60)
   :grey-70 (color/darken colour-white 70)
   :grey-80 (color/darken colour-white 80)
   :grey-90 (color/darken colour-white 90)
   :grey-95 (color/darken colour-white 95)
   :black-20-transparent (rgba 0 0 0 0.2)
   :black-50-transparent (rgba 0 0 0 0.5)
   :black-60-transparent (rgba 0 0 0 0.6)
   :black-70-transparent (rgba 0 0 0 0.7)
   :black-80-transparent (rgba 0 0 0 0.8)
   :black-90-transparent (rgba 0 0 0 0.9)
   :black-95-transparent (rgba 0 0 0 0.95)
   :blue (rgb 0 0 255)})

;; Fonts common to all themes
(def fonts
  {:default "\"Open Sans\", \"Helvetica Neue\", Helvetica, Arial, sans-serif"})

;; Font sizes common to all themes
(def font-sizes
  {:default "1.6rem"
   :x-small "1rem"
   :small "1.2rem"
   :medium-small "1.4rem"
   :medium (units/rem 1.7) ;; H6
   :medium-large (units/rem 1.8) ;; H5
   :large (units/rem 2.2) ;; H4
   :x-large (units/rem 2.6) ;; H3
   :xx-large (units/rem 3.0) ;; H2
   :xxx-large (units/rem 6.4) ;; H1
   })

;; Font weights common to all themes
(def font-weights
  {:regular 400
   :semi-bold 600
   :bold 700})

;; Line heights common to all themes
(def line-heights
  {:default 1.5
   :x-small 1
   :small 1.2
   :medium-small 1.2
   :medium (units/rem 2.1)
   :medium-large (units/rem 2.2)
   :large (units/rem 2.6)
   :x-large (units/rem 3.2)
   :xx-large (units/rem 4.4)
   :xxx-large (units/rem 6.8)})

;; Media common to all themes
(def media
  {:breakpoints [[:mobile 0]
                 [:tablet 768]
                 [:desktop 1025]
                 [:desktop-large 1680]]})

(defn get-breakpoint
  [key]
  ((keyword key) (into {} (:breakpoints media))))

(def min-width-desktop
  {:min-width (units/px (get-breakpoint :desktop))})

(def min-width-mobile
  {:min-width (units/px (get-breakpoint :mobile))})

(def min-width-tablet
  {:min-width (units/px (get-breakpoint :tablet))})

;; Arrow styles
(def ^:private arrow-colour (:grey-80 colours))

(defn arrow-mixin
  [{:keys [colour direction size]
    :or {colour (:grey-80 colours)
         direction :up
         size (units/rem 1)}
    :as props}]
  {:border-bottom-color (if (= :up direction) colour :transparent)
   :border-left-color (if (= :right direction) colour :transparent)
   :border-right-color (if (= :left direction) colour :transparent)
   :border-style :solid
   :border-top-color (if (= :down direction) colour :transparent)
   :border-width [[
                   (if (= :up direction) 0 size)
                   (if (= :right direction) 0 size)
                   (if (= :down direction) 0 size)
                   (if (= :left direction) 0 size)
                   ]]})

;; Max-width mixin
(def max-width-mixin {:margin [[0 :auto]]
                      :max-width (units/px (get-breakpoint :desktop))})
